import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, EyeIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import UserViewModal from './UserViewModal';
import UserEditModal from './UserEditModal';
import ConfirmModal from './ConfirmModal';
import { apiService, User, getAbsoluteUrl } from '../services/api';

interface UserManagementProps {
  onBack: () => void;
}

// API state
interface ApiState {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  loading: boolean;
  error: string | null;
}

const UserManagement: React.FC<UserManagementProps> = ({ onBack }) => {
  const [apiState, setApiState] = useState<ApiState>({
    users: [],
    pagination: null,
    loading: false,
    error: null,
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    user: User | null;
  }>({
    visible: false,
    user: null,
  });

  // State for pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const loadUsers = useCallback(async () => {
    setApiState(prev => ({ ...prev, loading: true, error: null }));
    try {
      // Handle isActive filter: convert string to boolean or undefined for 'All'
      let isActiveParam: boolean | undefined = undefined;
      if (statusFilter === 'true') {
        isActiveParam = true;
      } else if (statusFilter === 'false') {
        isActiveParam = false;
      }
      
      // If sorting by isActive, don't send sortBy to API (will sort client-side)
      const shouldSortClientSide = sortBy === 'isActive';
      
      const params: {
        page: number;
        limit: number;
        search?: string;
        sortBy?: 'id' | 'name' | 'email' | 'createdAt' | 'lastLogin';
        sortOrder?: 'asc' | 'desc';
        isActive?: boolean;
      } = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
      };
      
      // Only add sortBy and sortOrder if not sorting by isActive
      if (!shouldSortClientSide) {
        params.sortBy = sortBy as 'id' | 'name' | 'email' | 'createdAt' | 'lastLogin';
        params.sortOrder = sortOrder;
      }
      
      // Only add isActive if it has a value
      if (isActiveParam !== undefined) {
        params.isActive = isActiveParam;
      }
      
      const response = await apiService.getUsers(params);
      
      // Client-side sorting for isActive
      let sortedUsers = response.users || [];
      if (shouldSortClientSide && sortedUsers.length > 0) {
        sortedUsers = [...sortedUsers].sort((a, b) => {
          if (sortOrder === 'asc') {
            return a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1;
          } else {
            return a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1;
          }
        });
      }
      
      setApiState({
        users: sortedUsers,
        pagination: response.pagination,
        loading: false,
        error: null,
      });
    } catch (error) {
      setApiState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load users',
      }));
    }
  }, [currentPage, pageSize, searchTerm, sortBy, sortOrder, statusFilter]);

  // Load users on component mount and when pagination/search changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const confirmToggleUserStatus = async () => {
    if (!confirmModal.user) return;
    
      setIsLoading(true);
      setApiState(prev => ({ ...prev, error: null })); // Clear previous errors
      try {
      await apiService.toggleUserStatus(confirmModal.user.id);
        // Reload users to get updated data
        loadUsers();
      setConfirmModal({ visible: false, user: null });
      } catch (error) {
        console.error('Error toggling user status:', error);
        setApiState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to toggle user status',
        }));
      } finally {
        setIsLoading(false);
    }
  };

  const handleSaveUser = async (updatedUser: User) => {
    setIsLoading(true);
    setApiState(prev => ({ ...prev, error: null })); // Clear previous errors
    try {
      await apiService.updateUser(updatedUser.id, updatedUser);
      // Reload users to get updated data
      loadUsers();
      setIsEditModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      setApiState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update user',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <ChevronUpIcon className="w-4 h-4 text-gray-400" />;
    return sortOrder === 'asc' ? <ChevronUpIcon className="w-4 h-4 text-gray-600" /> : <ChevronDownIcon className="w-4 h-4 text-gray-600" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {    year: 'numeric',
      month: 'short',
      day: 'numeric', });
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getProviderBadge = (provider: string | null | undefined) => {
    if (!provider) return <span className="text-gray-500">Email</span>;
    
    const colors = {
      FACEBOOK: 'bg-blue-100 text-blue-800',
      GOOGLE: 'bg-green-100 text-green-800',
      APPLE: 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[provider as keyof typeof colors]}`}>
        {provider}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total Users: {apiState.pagination?.total || 0}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {apiState.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 text-left">Error</h3>
              <div className="mt-2 text-sm text-red-700">{apiState.error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('isActive')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Status</span>
                    {getSortIcon('isActive')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Created</span>
                    {getSortIcon('createdAt')}
                  </button>
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('lastLogin')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Last Login</span>
                    {getSortIcon('lastLogin')}
                  </button>
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apiState.loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : apiState.users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                apiState.users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          {user.avatar ? (
                            <img
                              src={getAbsoluteUrl(user.avatar)}
                              alt={user.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-600">
                              {(user.username ?? "A").charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 text-left">{user.name}</div>
                          <div className="text-sm text-gray-500 text-left">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      {user.phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {getProviderBadge(user.socialProvider)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {getStatusBadge(user.isActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                      {formatDate(user.createdAt)}
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt ? formatDate(user.createdAt) : '-'}
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {apiState.pagination && apiState.pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === apiState.pagination!.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{(currentPage -1) * pageSize + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, apiState.pagination!.total)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{apiState.pagination!.total}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  {Array.from(Array(Math.min(5, apiState.pagination!.totalPages)), (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-imola-primary border-imola-primary text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === apiState.pagination!.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View User Modal */}
      {isViewModalOpen && selectedUser && (
        <UserViewModal
          user={selectedUser}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && selectedUser && (
        <UserEditModal
          user={selectedUser}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
          }}
          onSave={handleSaveUser}
          isLoading={isLoading}
          error={apiState.error}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal.visible && confirmModal.user && (
        <ConfirmModal
          visible={confirmModal.visible}
          title="Confirm Action"
          message={`Are you sure you want to ${confirmModal.user.isActive ? 'deactivate' : 'activate'} ${confirmModal.user.name}?`}
          confirmText={confirmModal.user.isActive ? 'Deactivate' : 'Activate'}
          cancelText="Cancel"
          confirmButtonClass={confirmModal.user.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
          onConfirm={confirmToggleUserStatus}
          onCancel={() => setConfirmModal({ visible: false, user: null })}
        />
      )}
    </div>
  );
};

export default UserManagement; 